/**
 * React side of the live chat (P8). The shell mounts `useChatLiveConnection` once; pages read
 * `useChatLiveStatus` (polling stops while live) and `useThreadTyping` / `notifyTyping`.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { chatLive, chatToken } from '../index';
import { SUPPORT_DRAFT_ID, type Message, type Thread } from '../domain';
import { qk } from '../queries/keys';
import type { ChatEvent } from './chatSocket';

type MessageEvent = Extract<ChatEvent, { type: 'message' }>;

/**
 * Applies a live message to the cached thread list. Returns the new list (or null: unchanged), whether
 * the list must be re-read from the server (unknown thread; bot or system message, which move the
 * thread's state and choices) and the incoming message to announce (not the client's own).
 */
export function applyLiveMessage(
  threads: Thread[] | undefined,
  event: MessageEvent,
  viewing: boolean,
): { threads: Thread[] | null; reconcile: boolean; incoming: { thread: Thread; message: Message } | null } {
  const fromThem = event.author !== 'client';
  const index = threads?.findIndex((t) => t.id === event.threadId) ?? -1;
  const thread = index >= 0 ? threads?.[index] : undefined;
  if (!threads || !thread) return { threads: null, reconcile: true, incoming: null };
  if (thread.messages.some((m) => m.id === event.message.id)) return { threads: null, reconcile: false, incoming: null };
  const count = (thread.unreadCount ?? (thread.unread ? 1 : 0)) + (fromThem && !viewing ? 1 : 0);
  const next: Thread = {
    ...thread,
    messages: event.message.text.trim() ? [...thread.messages, event.message] : thread.messages,
    time: event.message.time,
    unread: count > 0,
    unreadCount: count,
  };
  const list = [next, ...threads.filter((t) => t.id !== thread.id)];
  return {
    threads: list,
    reconcile: event.author === 'bot' || event.author === 'system',
    incoming: fromThem ? { thread: next, message: event.message } : null,
  };
}

const noop = () => () => {};
const subscribeStatus = (fn: () => void) =>
  chatLive ? chatLive.subscribe((e) => (e.type === 'status' ? fn() : undefined)) : noop();
const liveSnapshot = () => chatLive?.isLive() ?? false;

/** True while the socket is connected and accepted by the server (the 3 s polling then stops). */
export function useChatLiveStatus(): boolean {
  return useSyncExternalStore(subscribeStatus, liveSnapshot, () => false);
}

/** The other side is writing in this conversation (live only). */
const subscribeTyping = (fn: () => void) =>
  chatLive ? chatLive.subscribe((e) => (e.type === 'typing' || e.type === 'status' ? fn() : undefined)) : noop();

export function useThreadTyping(threadId: string | undefined): boolean {
  return useSyncExternalStore(
    subscribeTyping,
    () => (!!threadId && chatLive?.isTyping(threadId)) || false,
    () => false,
  );
}

/** Tells the other side the client is writing (debounced stop after 2 s). */
export function notifyTyping(threadId: string, stop = false): void {
  if (!chatLive || threadId === SUPPORT_DRAFT_ID) return;
  if (stop) chatLive.stopTyping(threadId);
  else chatLive.typing(threadId);
}

interface ConnectionOptions {
  /** Signed-in client id, or null (no connection). */
  userId: string | null;
  /** Conversations to listen to (their rooms are joined, and re-joined after each reconnection). */
  threadIds: string[];
  /** The client is looking at this conversation (no unread count, no announcement). */
  isViewing: (threadId: string) => boolean;
  /** A message from support, the bot or an agency arrived in a conversation the client is not viewing. */
  onIncoming: (thread: Thread, message: Message) => void;
}

/** One connection per session: opened at sign-in, rebuilt when the account changes, closed at logout. */
export function useChatLiveConnection({ userId, threadIds, isViewing, onIncoming }: ConnectionOptions) {
  const qc = useQueryClient();
  const callbacks = useRef({ isViewing, onIncoming });
  useEffect(() => {
    callbacks.current = { isViewing, onIncoming };
  });

  useEffect(() => {
    if (!chatLive) return;
    const token = userId ? chatToken() : null;
    if (userId && token) chatLive.start({ userId, token });
    else chatLive.stop();
  }, [userId]);
  useEffect(() => () => chatLive?.stop(), []);

  const rooms = threadIds.filter((id) => id && id !== SUPPORT_DRAFT_ID).join(',');
  useEffect(() => {
    if (!chatLive || !rooms) return;
    for (const id of rooms.split(',')) chatLive.join(id);
  }, [rooms, userId]);

  useEffect(() => {
    if (!chatLive) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const reconcile = (ms = 300) => {
      clearTimeout(timer);
      timer = setTimeout(() => void qc.invalidateQueries({ queryKey: qk.threads }), ms);
    };
    const off = chatLive.subscribe((e) => {
      switch (e.type) {
        case 'typing':
          return;
        case 'status':
          // Connected: catch up on what was missed; disconnected: the polling takes over.
          if (e.live) reconcile(0);
          return;
        case 'message': {
          const viewing = callbacks.current.isViewing(e.threadId);
          const res = applyLiveMessage(qc.getQueryData<Thread[]>(qk.threads), e, viewing);
          if (res.threads) qc.setQueryData(qk.threads, res.threads);
          if (res.reconcile) reconcile();
          if (res.incoming && !viewing) callbacks.current.onIncoming(res.incoming.thread, res.incoming.message);
          return;
        }
        default:
          reconcile();
      }
    });
    return () => {
      off();
      clearTimeout(timer);
    };
  }, [qc]);
}

/** A live connection exists in this build (http data source); the demo data keeps the polling. */
export const chatLiveAvailable = !!chatLive;
